import { html, css } from 'lit';

import '../../../packages/neb-lit-components/src/components/inputs/neb-select-search';
import { PAYER_PLAN_VERSION_CLAIMS } from '../../../packages/neb-api-client/src/payer-plan-api-client';
import { PayerPlanQueryService } from '../../../packages/neb-api-client/src/services/payer-plan';
import { BUTTON_ROLE } from '../../../packages/neb-lit-components/src/components/neb-button';
import NebPopup from '../../../packages/neb-popup/src/neb-popup';
import Debouncer from '../../../packages/neb-utils/debouncer';
import {
  INSURANCE_TYPE_CODE_ITEMS,
  isWorkersComp,
  isMedicareSecondary,
  INSURED_RELATIONSHIP,
} from '../../../packages/neb-utils/patientInsurance';
import { CSS_FONT_FAMILY, CSS_SPACING } from '../../styles';

export const ELEMENTS = {
  payerDropdown: {
    id: 'payer-dropdown',
  },
  buttonReplace: {
    id: 'button-replace',
  },
  buttonCancel: {
    id: 'button-cancel',
  },
  message: {
    id: 'popup-message',
  },
  medicareTypeCodeDropdown: {
    id: 'medicare-type-code-dropdown',
  },
  insuredNameField: {
    id: 'insured-name-field',
  },
};

class NebPopupReplaceInsurancePayer extends NebPopup {
  static get properties() {
    return {
      __payerPlans: {
        type: Array,
      },
      __payerSearchValue: {
        type: String,
      },
      __selectedPayerPlanId: {
        type: String,
      },
      __selectedInsuranceTypeCode: {
        type: String,
      },
      __insuredName: {
        type: String,
      },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host {
          width: 600px;
          font-family: ${CSS_FONT_FAMILY};
        }

        :host([layout='small']) {
          width: 350px;
        }

        .content-container {
          display: flex;
          flex-direction: column;
          flex: 1 0 0;
          justify-content: space-between;
          height: 100%;
          gap: 10px;
        }

        .button-container {
          display: flex;
          justify-content: start;
          margin-top: 30px;
        }

        .spacing {
          margin-right: 10px;
        }

        .padding-top {
          padding-top: ${CSS_SPACING};
        }

        .italic-text {
          font-style: italic;
        }
      `,
    ];
  }

  initState() {
    super.initState();

    this.title = 'Replace Payer';
    this.model = {
      payerPlan: {},
      planInfo: {},
    };

    this.__payerPlans = [];
    this.__payerSearchValue = '';
    this.__selectedPayerPlanId = '';
    this.__selectedInsuranceTypeCode = '';
    this.__insuredName = '';
    this.__query = {
      hideInactive: true,
      search: '',
    };

    this.__searchDebouncer = new Debouncer(() => {
      this.__payerPlanQueryService.update(this.__query);
    });
  }

  initHandlers() {
    super.initHandlers();
    this.handlers = {
      ...this.handlers,
      confirm: () => {
        if (this.__checkRequiredFieldsEmpty()) {
          return;
        }

        this.onClose({
          payerPlanId: this.__selectedPayerPlanId,
          insuranceTypeCode: this.__selectedInsuranceTypeCode,
          insuredName: this.__insuredName,
        });
      },
      payerSelected: ({ value }) => {
        this.__selectedPayerPlanId = value?.id || '';
        this.__selectedInsuranceTypeCode = this.model?.insuranceTypeCode;
        this.__insuredName = this.model?.policyHolder?.insuredName;
      },
      search: ({ value: search }) => {
        if (search !== this.__payerSearchValue) {
          this.__payerSearchValue = search;
          this.__query.search = search;

          this.__searchDebouncer.debounce();
        }
      },
      changeInsuranceTypeCode: ({ value }) => {
        this.__selectedInsuranceTypeCode = value?.data?.code || '';
      },
      changeInsuredName: ({ value }) => {
        this.__insuredName = value;
      },
    };
  }

  __checkRequiredFieldsEmpty() {
    return (
      !this.__selectedPayerPlanId ||
      (this.__shouldRenderMedicareTypeCode() &&
        !this.__selectedInsuranceTypeCode) ||
      (this.__shouldRenderInsuredName() && !this.__insuredName)
    );
  }

  __setupPayerPlanQueryService() {
    this.__payerPlanQueryService = new PayerPlanQueryService(
      ({ model: payerPlans }) => {
        this.__payerPlans = payerPlans
          .filter(payer => payer.id !== this.model.payerPlan.id)
          .map(payer => ({
            ...payer,
            label: `(${payer.alias}) ${
              payer.payerName ? `${payer.payerName}` : ''
            }`,
          }));
      },
      PAYER_PLAN_VERSION_CLAIMS,
    );
  }

  __getSelectedPayer() {
    return this.__payerPlans.find(
      payer => payer.id === this.__selectedPayerPlanId,
    );
  }

  __getPopupMessage() {
    return html`
      <div id="${ELEMENTS.message.id}">
        Replacing the payer will copy details from the plan
        <b class="italic-text">${this.model.planInfo.planName}</b> and create a
        new one with the updated payer. The original plan will be inactivated.
      </div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();

    this.__setupPayerPlanQueryService();

    this.__payerPlanQueryService.update(this.__query);
  }

  __getSelectedPayerPlan() {
    return this.__payerPlans.find(
      payer => payer.id === this.__selectedPayerPlanId,
    );
  }

  __getSelectedInsuranceTypeCode() {
    return INSURANCE_TYPE_CODE_ITEMS.find(
      item => item.data.code === this.__selectedInsuranceTypeCode,
    );
  }

  __shouldRenderMedicareTypeCode() {
    if (!this.__selectedPayerPlanId) {
      return false;
    }

    const newPayerPlan = this.__getSelectedPayerPlan();
    const isFieldRequired = isMedicareSecondary(
      newPayerPlan.financialClass,
      this.model.defaultLevel,
    );

    if (!isFieldRequired) {
      return false;
    }

    return true;
  }

  __renderMedicareTypeCode() {
    if (!this.__shouldRenderMedicareTypeCode()) return '';

    return html`
      <neb-select
        id="${ELEMENTS.medicareTypeCodeDropdown.id}"
        class="column-two-three"
        name="insuranceTypeCode"
        label="Medicare Type Code"
        helper="Required"
        .items="${INSURANCE_TYPE_CODE_ITEMS}"
        .value="${this.__getSelectedInsuranceTypeCode()}"
        .onChange="${this.handlers.changeInsuranceTypeCode}"
        wrapText
      ></neb-select>
    `;
  }

  __shouldRenderInsuredName() {
    if (!this.__selectedPayerPlanId) {
      return false;
    }

    const newPayerPlan = this.__getSelectedPayerPlan();
    const isFieldRequired =
      this.model.insuredRelationship !== INSURED_RELATIONSHIP.Self &&
      !this.model.policyHolder?.insuredName &&
      isWorkersComp(newPayerPlan);

    if (!isFieldRequired) {
      return false;
    }

    return true;
  }

  __renderInsuredName() {
    if (!this.__shouldRenderInsuredName()) return '';

    return html`
      <neb-textfield
        id="${ELEMENTS.insuredNameField.id}"
        class="column-one-three"
        name="insuredName"
        label="Insured Name"
        helper="Required"
        maxLength="255"
        .value="${this.__insuredName}"
        .onChange="${this.handlers.changeInsuredName}"
      ></neb-textfield>
    `;
  }

  __renderPayerDropdown() {
    return html`
      <neb-select-search
        id="${ELEMENTS.payerDropdown.id}"
        name="payerPlanId"
        class="search"
        label="Payer"
        helper="Required"
        .items="${this.__payerPlans}"
        .search="${this.__payerSearchValue}"
        .value="${this.__getSelectedPayer()}"
        .onChange="${this.handlers.payerSelected}"
        .onSearch="${this.handlers.search}"
        showSearch
      ></neb-select-search>
    `;
  }

  renderContent() {
    return html`
      <div class="content-container">
        ${this.__getPopupMessage()} ${this.__renderPayerDropdown()}
        ${this.__renderMedicareTypeCode()} ${this.__renderInsuredName()}

        <div class="button-container">
          <neb-button
            id="${ELEMENTS.buttonReplace.id}"
            class="spacing"
            label="Replace"
            .role="${BUTTON_ROLE.CONFIRM}"
            .onClick="${this.handlers.confirm}"
            ?disabled="${this.__checkRequiredFieldsEmpty()}"
          ></neb-button>

          <neb-button
            id="${ELEMENTS.buttonCancel.id}"
            label="Cancel"
            .role="${BUTTON_ROLE.OUTLINE}"
            .onClick="${this.handlers.cancel}"
          ></neb-button>
        </div>
      </div>
    `;
  }
}

customElements.define(
  'neb-popup-replace-insurance-payer',
  NebPopupReplaceInsurancePayer,
);
